import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { 
  Building2, 
  BookOpen, 
  Calendar, 
  ImageIcon, 
  Users,
  Palette,
  Settings,
  ExternalLink,
  Mail,
  Globe,
  Plus,
  UserPlus,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorDashboard.css';

const OrganizationDetail = () => {
  const { orgSlug } = useParams();
  const context = useOutletContext();
  
  const [organization, setOrganization] = useState(null);
  const [collections, setCollections] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [stats, setStats] = useState({
    totalCollections: 0,
    totalArtworks: 0,
    totalEvents: 0,
    totalMembers: 0,
    totalArtists: 0
  });
  const [loading, setLoading] = useState(true);

  // Helper to add Cloudinary transformation
  const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };

  useEffect(() => {
    if (orgSlug) {
      loadOrganizationData();
    }
  }, [orgSlug]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);

      // Fetch organization by slug
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('slug', orgSlug)
        .single();

      if (orgError) {
        console.error('Error fetching organization:', orgError);
        setLoading(false);
        return;
      }

      setOrganization(org);

      // Fetch collections, events, and members in parallel
      const [collectionsResult, eventsResult, membersResult] = await Promise.all([
        supabase
          .from('collections')
          .select('*')
          .eq('organization_id', org.organization_id)
          .eq('access_control', 'Public')
          .order('created_at', { ascending: false }),
        supabase
          .from('events')
          .select('*')
          .eq('organization_id', org.organization_id)
          .order('event_start', { ascending: false }),
        supabase
          .from('organization_members')
          .select(`
            *,
            users:user_uuid (
              uuid,
              first_name,
              last_name,
              email,
              profile_img
            )
          `)
          .eq('organization_id', org.organization_id)
          .eq('is_active', true)
      ]);

      const collectionsData = collectionsResult.data || [];
      const eventsData = eventsResult.data || [];
      const membersData = membersResult.data || [];

      setCollections(collectionsData);
      setEvents(eventsData);
      setMembers(membersData);

      // Check current user's role in this organization
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const currentMember = membersData.find(m => m.user_uuid === user.id);
        if (currentMember) {
          setCurrentUserRole(currentMember.role);
        }
      }

      // Get artwork count from collection_vc_mapping (only Public artworks)
      let totalArtworks = 0;
      let totalArtists = 0;
      if (collectionsData.length > 0) {
        const collectionIds = collectionsData.map(c => c.collection_id);
        
        // Get artwork IDs from collection mappings
        const { data: artworkData } = await supabase
          .from('collection_vc_mapping')
          .select('vc_id')
          .in('collection_id', collectionIds);
        
        if (artworkData && artworkData.length > 0) {
          const vcIds = artworkData.map(a => a.vc_id);
          
          // Count only Public artworks and get distinct artists
          const { data: publicArtworks } = await supabase
            .from('vcs')
            .select('vc_id, uuid')
            .in('vc_id', vcIds)
            .eq('access_control', 'Public');
          
          if (publicArtworks) {
            totalArtworks = publicArtworks.length;
            const uniqueArtists = new Set(
              publicArtworks
                .filter(a => a.uuid)
                .map(a => a.uuid)
            );
            totalArtists = uniqueArtists.size;
          }
        }
      }

      setStats({
        totalCollections: collectionsData.length,
        totalArtworks,
        totalEvents: eventsData.length,
        totalMembers: membersData.length,
        totalArtists
      });

    } catch (error) {
      console.error('Error loading organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="curator-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading organization...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="curator-overview">
        <div className="empty-state">
          <h2>Organization Not Found</h2>
          <p>The organization "{orgSlug}" could not be found.</p>
          <Link to="/curator/dashboard" className="back-link">
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-overview org-detail-page">
      {/* Header with org info */}
      <div className="org-detail-header">
        <div className="org-header-content">
          <div className="org-header-info">
            <h1 className="org-title">{organization.name}</h1>
            {organization.description && (
              <p className="org-description">{organization.description}</p>
            )}
            <div className="org-meta">
              {organization.website && (
                <a href={organization.website} target="_blank" rel="noopener noreferrer" className="org-meta-link">
                  <Globe size={14} />
                  Website
                </a>
              )}
              {organization.contact_email && (
                <a href={`mailto:${organization.contact_email}`} className="org-meta-link">
                  <Mail size={14} />
                  {organization.contact_email}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row - Clickable Cards */}
      <div className="org-stats-row clickable">
        <Link to="/curator/collections" className="org-stat-item clickable">
          <BookOpen size={20} />
          <span className="org-stat-number">{stats.totalCollections}</span>
          <span className="org-stat-label">Collections</span>
        </Link>
        <Link to="/curator/artworks" className="org-stat-item clickable">
          <ImageIcon size={20} />
          <span className="org-stat-number">{stats.totalArtworks}</span>
          <span className="org-stat-label">Artworks</span>
        </Link>
        <Link to="/curator/artists" className="org-stat-item clickable">
          <Palette size={20} />
          <span className="org-stat-number">{stats.totalArtists}</span>
          <span className="org-stat-label">Artists</span>
        </Link>
        <Link 
          to={`/curator/organization/${orgSlug}/team`} 
          className="org-stat-item clickable"
        >
          <Users size={20} />
          <span className="org-stat-number">{stats.totalMembers}</span>
          <span className="org-stat-label">Team Members</span>
        </Link>
      </div>

      {/* Recent Collections Preview */}
      <div className="org-section">
        <div className="org-section-header">
          <h2>Collections</h2>
          <div className="org-section-actions">
            <Link to={`/curator/collections/new?org=${organization.organization_id}`} className="btn-icon">
              <Plus size={18} />
            </Link>
            {stats.totalCollections > 3 && (
              <Link to="/curator/collections" className="view-all-link">
                View All <ChevronRight size={16} />
              </Link>
            )}
          </div>
        </div>
        <div className="org-collections-grid">
          {collections.length === 0 ? (
            <div className="empty-section-state">
              <p>No collections yet</p>
              <Link to={`/curator/collections/new?org=${organization.organization_id}`} className="create-btn">
                <Plus size={16} />
                Create Collection
              </Link>
            </div>
          ) : (
            collections.slice(0, 4).map(collection => (
              <Link 
                key={collection.collection_id} 
                to={`/curator/collections/${collection.collection_id}`}
                className="org-collection-card"
              >
                <div className="collection-card-img">
                  {collection.cover_image ? (
                    <img src={addCloudinaryTransform(collection.cover_image, 't_fitmax-300/')} alt={collection.title} />
                  ) : (
                    <div className="collection-placeholder">
                      <BookOpen size={24} />
                    </div>
                  )}
                </div>
                <div className="collection-card-info">
                  <h3>{collection.title}</h3>
                  <p>{collection.description || 'No description'}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Recent Events Preview */}
      {events.length > 0 && (
        <div className="org-section">
          <div className="org-section-header">
            <h2>Upcoming Events</h2>
            {stats.totalEvents > 3 && (
              <Link to="/curator/events" className="view-all-link">
                View All <ChevronRight size={16} />
              </Link>
            )}
          </div>
          <div className="org-events-list">
            {events.slice(0, 3).map(event => (
              <Link 
                key={event.id} 
                to={`/curator/events/${event.id}`}
                className="org-event-item"
              >
                <div className="event-date">
                  <Calendar size={16} />
                  {event.event_start ? new Date(event.event_start).toLocaleDateString() : 'TBD'}
                </div>
                <div className="event-info">
                  <h3>{event.name}</h3>
                  <p>{event.venue_name || event.venue_address || 'Location TBD'}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Team Members Preview */}
      <div className="org-section">
        <div className="org-section-header">
          <h2>Team Members</h2>
          <div className="org-section-actions">
            {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
              <Link to={`/curator/organization/${orgSlug}/team`} className="btn-icon" title="Manage Team">
                <UserPlus size={18} />
              </Link>
            )}
          </div>
        </div>
        <div className="org-members-list">
          {members.length === 0 ? (
            <div className="empty-section-state">
              <p>No team members yet</p>
            </div>
          ) : (
            members.slice(0, 4).map(member => (
              <div key={member.id} className="org-member-item">
                <div className="member-avatar">
                  {member.users?.profile_img ? (
                    <img src={addCloudinaryTransform(member.users.profile_img, 't_square-150/')} alt="" />
                  ) : (
                    <div className="member-avatar-placeholder">
                      {member.users?.first_name?.[0] || member.users?.email?.[0] || '?'}
                    </div>
                  )}
                </div>
                <div className="member-info">
                  <h4>
                    {member.users?.first_name && member.users?.last_name 
                      ? `${member.users.first_name} ${member.users.last_name}`
                      : member.users?.email || 'Unknown'}
                  </h4>
                  <span className="member-role">{member.role}</span>
                </div>
              </div>
            ))
          )}
          {members.length > 4 && (
            <Link to={`/curator/organization/${orgSlug}/team`} className="view-all-link" style={{ marginTop: '1rem' }}>
              View All {stats.totalMembers} Members <ChevronRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetail;
